// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import {
  TRIAGE_SORT_COLUMN_KEYS,
  type TriageSortColumnKey,
  type TriageSortDirection,
  type TriageSortState,
} from '@polarissleep/shared';
import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router';

const SORT_PARAM = 'sort';
const DIR_PARAM = 'dir';
const PAGE_PARAM = 'page';
const SEARCH_PARAM = 'q';
const HIDE_TEST_PARAM = 'hideTest';

const VALID_DIRECTIONS = new Set<string>(['asc', 'desc']);
const VALID_COLUMN_KEYS = new Set<string>(TRIAGE_SORT_COLUMN_KEYS);

/**
 * Convert a mantine-datatable `DataTableSortStatus`-shaped object to a `TriageSortState`.
 * Returns `null` when the columnAccessor is not a valid `TriageSortColumnKey`.
 */
export function sortStatusToTriageSortState(status: {
  columnAccessor: string;
  direction: 'asc' | 'desc';
}): TriageSortState | null {
  if (!VALID_COLUMN_KEYS.has(status.columnAccessor)) {
    return null;
  }
  return {
    column: status.columnAccessor as TriageSortColumnKey,
    direction: status.direction as TriageSortDirection,
  };
}

/**
 * Convert a `TriageSortState` to a mantine-datatable `DataTableSortStatus`-shaped object.
 * Returns `null` when `state` is `null`.
 */
export function triageSortStateToSortStatus(
  state: TriageSortState | null
): { columnAccessor: string; direction: 'asc' | 'desc' } | null {
  if (!state) {
    return null;
  }
  return {
    columnAccessor: state.column,
    direction: state.direction,
  };
}

/**
 * Parse an integer page number from a raw URL string value.
 * Returns `null` when the value is absent, non-integer, 0, or negative.
 */
function parsePageParam(raw: string | null): number | null {
  if (raw === null) {
    return null;
  }
  const parsed = Number(raw);
  if (!Number.isInteger(parsed) || parsed < 1) {
    return null;
  }
  return parsed;
}

export interface TriageQueueUrlState {
  sortState: TriageSortState | null;
  page: number;
  search: string;
  hideTestPatients: boolean;
  setSortState: (next: TriageSortState | null) => void;
  setPage: (n: number) => void;
  setSearch: (next: string) => void;
  setHideTestPatients: (v: boolean) => void;
}

/**
 * Parse and persist `?sort=<column-key>&dir=asc|desc&page=<n>` in the URL via
 * `react-router-dom` `useSearchParams`. Invalid combos are stripped via
 * `replaceState` on mount.
 *
 * Behavioral coupling: calling `setSortState` with a non-null value when the
 * current page is > 1 also resets the page to 1 in a single URL update so
 * back/forward navigation sees the sort + page together.
 *
 * `setPage(1)` omits the `page` param from the URL (keeps URLs clean for the
 * default page). `setPage(n)` where `n > 1` writes `?page=<n>`.
 */
export function useTriageQueueUrlState(): TriageQueueUrlState {
  const [searchParams, setSearchParams] = useSearchParams();

  const rawSort = searchParams.get(SORT_PARAM);
  const rawDir = searchParams.get(DIR_PARAM);
  const rawPage = searchParams.get(PAGE_PARAM);
  const rawSearch = searchParams.get(SEARCH_PARAM);
  const rawHideTest = searchParams.get(HIDE_TEST_PARAM);
  const hideTestPatients = rawHideTest === null || rawHideTest === '1';

  const isValidSort = rawSort !== null && VALID_COLUMN_KEYS.has(rawSort);
  const isValidDir = rawDir !== null && VALID_DIRECTIONS.has(rawDir);
  const hasSortParams = rawSort !== null || rawDir !== null;
  const isBothValid = isValidSort && isValidDir;

  const parsedPage = parsePageParam(rawPage);
  const hasInvalidPage = rawPage !== null && parsedPage === null;

  // Strip invalid combos on mount. Both must be present and valid, or both are
  // cleared. Invalid page values are also stripped.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally runs once on mount only — strips invalid URL params at startup
  useEffect(() => {
    const needsSortStrip = hasSortParams && !isBothValid;
    const needsPageStrip = hasInvalidPage;
    if (needsSortStrip || needsPageStrip) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (needsSortStrip) {
            next.delete(SORT_PARAM);
            next.delete(DIR_PARAM);
          }
          if (needsPageStrip) {
            next.delete(PAGE_PARAM);
          }
          return next;
        },
        { replace: true }
      );
    }
  }, []);

  // Memoize on primitive raw param values so the returned object reference is
  // stable across renders when the URL is unchanged. Without this, every render
  // produces a fresh `sortState` literal, which propagates through
  // `useTriageQueue`'s `useCallback([sort, ...])` dep array and triggers an
  // infinite fetch loop.
  const sortState: TriageSortState | null = useMemo(
    () => (isBothValid ? { column: rawSort as TriageSortColumnKey, direction: rawDir as TriageSortDirection } : null),
    [isBothValid, rawSort, rawDir]
  );

  // page=1 is the default; omit from URL when that is the value.
  const page = parsedPage ?? 1;

  // search empty string is the default; omit from URL when that is the value.
  const search = rawSearch ?? '';

  const setSortState = useCallback(
    (next: TriageSortState | null) => {
      setSearchParams(
        (prev) => {
          const updated = new URLSearchParams(prev);
          if (!next) {
            updated.delete(SORT_PARAM);
            updated.delete(DIR_PARAM);
          } else {
            updated.set(SORT_PARAM, next.column);
            updated.set(DIR_PARAM, next.direction);
            // Sorting always restarts from page 1 — reset page param.
            updated.delete(PAGE_PARAM);
          }
          return updated;
        },
        { replace: false }
      );
    },
    [setSearchParams]
  );

  const setPage = useCallback(
    (n: number) => {
      setSearchParams(
        (prev) => {
          const updated = new URLSearchParams(prev);
          if (n <= 1) {
            updated.delete(PAGE_PARAM);
          } else {
            updated.set(PAGE_PARAM, String(n));
          }
          return updated;
        },
        { replace: false }
      );
    },
    [setSearchParams]
  );

  const setSearch = useCallback(
    (next: string) => {
      setSearchParams(
        (prev) => {
          const updated = new URLSearchParams(prev);
          if (next === '') {
            updated.delete(SEARCH_PARAM);
          } else {
            updated.set(SEARCH_PARAM, next);
          }
          // Search always restarts from page 1 — reset page param.
          updated.delete(PAGE_PARAM);
          return updated;
        },
        { replace: false }
      );
    },
    [setSearchParams]
  );

  const setHideTestPatients = useCallback(
    (v: boolean) => {
      setSearchParams(
        (prev) => {
          const updated = new URLSearchParams(prev);
          updated.set(HIDE_TEST_PARAM, v ? '1' : '0');
          updated.delete(PAGE_PARAM);
          return updated;
        },
        { replace: false }
      );
    },
    [setSearchParams]
  );

  return { sortState, page, search, hideTestPatients, setSortState, setPage, setSearch, setHideTestPatients };
}
